import React from "react";
import { Sparkles, Ticket, Zap, Star } from "lucide-react";
import { Reveal, SectionHeading, SmartImage } from "./common";
import { IMAGES, CONTACT } from "../data";
import BookingWidget from "./BookingWidget";

const PriceFeatures = () => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <div className="rounded-2xl border border-vivid-magenta/30 bg-vivid-magenta/5 p-5 text-center">
        <p className="text-sm text-white/60">Adult</p>
        <p className="font-heading text-3xl font-bold text-white">$49</p>
        <p className="text-xs text-white/50">per person</p>
      </div>
      <div className="rounded-2xl border border-vivid-cyan/30 bg-vivid-cyan/5 p-5 text-center">
        <p className="text-sm text-white/60">Child</p>
        <p className="font-heading text-3xl font-bold text-white">$35</p>
        <p className="text-xs text-white/50">per child</p>
      </div>
    </div>
    <ul className="mt-4 space-y-2 text-sm text-white/70">
      <li>✓ Front row Vivid Sydney light installation views</li>
      <li>✓ Fully licensed bar on board</li>
      <li>✓ Spacious indoor & outdoor viewing areas</li>
      <li>✓ Departs from King Street Wharf, Wharf 3 · <a href={CONTACT.directions} target="_blank" rel="noopener noreferrer" data-testid="ticket-directions-link" className="text-vivid-cyan hover:underline">Get directions</a></li>
    </ul>
  </>
);

/* Compact booking band placed high on the page */
export const TopBooking = () => (
  <section id="book" data-testid="top-booking-section" className="relative py-16 sm:py-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-vivid-cyan">
              <Sparkles className="h-4 w-4" /> Book in seconds
            </span>
            <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Reserve your Vivid Cruise Sydney tickets</h2>
          </div>
          <p className="max-w-md text-sm text-white/60">
            Adults <span className="font-semibold text-white">$49</span> · Kids <span className="font-semibold text-white">$35</span>, secure your Vivid Cruises Sydney Harbour seats before evening sailings sell out.
          </p>
        </div>
      </Reveal>
      <div className="grid items-stretch gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <Reveal>
          <BookingWidget prefix="top-" />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex h-full flex-col gap-6">
            <SmartImage image={IMAGES.operaMagenta} caption className="min-h-[220px] flex-1 rounded-3xl border border-white/10" />
            <PriceFeatures />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* Full tickets section (nav target), high impact, standout design */
const TicketBooking = () => (
  <section id="tickets" data-testid="tickets-section" className="relative overflow-hidden py-24 sm:py-32">
    {/* glowing background blobs */}
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute left-1/4 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-vivid-magenta/25 blur-[120px]" />
      <div className="absolute right-1/4 bottom-10 h-72 w-72 translate-x-1/2 rounded-full bg-vivid-cyan/20 blur-[120px]" />
    </div>

    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-vivid-magenta/40 bg-vivid-magenta/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white">
            <Zap className="h-4 w-4 text-vivid-magenta" /> Vivid Sydney 2027 · Selling fast
          </span>
        </div>
        <SectionHeading
          eyebrow="Buy Vivid Cruise Sydney tickets"
          title="Secure your Vivid Cruise Sydney Tickets"
          sub="Book your Vivid Cruises Sydney Harbour tickets instantly with secure card payment. Adults $49, kids $35, or enquire about private Vivid cruises Sydney charters and group bookings."
          center
        />
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-sm text-white/70">
          <span className="flex items-center gap-1">{[...Array(5)].map((_, i) => (<Star key={i} className="h-4 w-4 fill-vivid-magenta text-vivid-magenta" />))}</span>
          <span>Rated 4.9 by 687+ Vivid Cruises Sydney guests</span>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <Reveal>
          <BookingWidget />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex h-full flex-col gap-6">
            <SmartImage image={IMAGES.deckDusk} caption className="h-56 rounded-3xl border border-white/10 sm:h-64" />
            <PriceFeatures />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default TicketBooking;
